import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ProfileModal.module.css";
import {
  fetchProfileMe,
  presignAvatarUpload,
  saveAvatarKey,
  uploadToPresignedUrl,
} from "./avatarApi";

type Props = {
  open: boolean;
  onClose: () => void;
  displayName: string;
  email: string;
  initials: string;
  avatarUrl: string | null;
  isProvider: boolean;
  onAvatarUpdated: (nextUrl: string | null) => void;
  onSignOut: () => void;
};

function extFromFile(file: File): string {
  const name = file.name || "";
  const idx = name.lastIndexOf(".");
  if (idx >= 0 && idx < name.length - 1) return name.slice(idx + 1).toLowerCase();
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  if (file.type === "image/jpeg") return "jpg";
  return "jpg";
}

export default function ProfileModal({
  open,
  onClose,
  displayName,
  email,
  initials,
  avatarUrl,
  isProvider,
  onAvatarUpdated,
  onSignOut,
}: Props) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState<string | null>(avatarUrl);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setResolvedAvatarUrl(avatarUrl);
  }, [open, avatarUrl]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const me = await fetchProfileMe();
      if (!alive || !me) return;
      if (me.avatarUrl) {
        setResolvedAvatarUrl(me.avatarUrl);
        onAvatarUpdated(me.avatarUrl);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, onAvatarUpdated]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const title = useMemo(() => (isProvider ? "Clinician profile" : "Patient profile"), [isProvider]);
  const subtitle = useMemo(() => (email ? email : "Account"), [email]);

  if (!open) return null;

  const onOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      setErr("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr("Image too large (max 5MB).");
      return;
    }

    setBusy(true);
    setErr(null);
    try {
      const extension = extFromFile(file);
      const presign = await presignAvatarUpload({
        contentType: file.type || "application/octet-stream",
        extension,
      });
      await uploadToPresignedUrl({ uploadUrl: presign.uploadUrl, file });
      await saveAvatarKey({ objectKey: presign.objectKey });

      const nextMe = await fetchProfileMe();
      const nextUrl = nextMe?.avatarUrl || null;
      setResolvedAvatarUrl(nextUrl);
      onAvatarUpdated(nextUrl);
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : "Failed to upload image";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const dashboardHref = isProvider ? "/dashboard" : "/patient-dashboard";
  const profileHref = "/profile";
  const settingsHref = "/profile/settings";

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={onOverlayMouseDown}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.sub}>{subtitle}</p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.row}>
            <div className={styles.avatar} aria-hidden="true">
              {resolvedAvatarUrl ? <img src={resolvedAvatarUrl} alt="" /> : initials || "?"}
            </div>
            <div className={styles.rowText}>
              <p className={styles.name}>{displayName || "Account"}</p>
              <p className={styles.email}>{email}</p>
            </div>
          </div>

          <div className={styles.actions}>
            <div className={styles.btnRow}>
              <a className={styles.btn} href={dashboardHref} onClick={onClose}>
                Dashboard
              </a>
              <a className={styles.btn} href={profileHref} onClick={onClose}>
                Profile
              </a>
              <a className={`${styles.btn} ${styles.btnPrimary}`} href={settingsHref} onClick={onClose}>
                Settings
              </a>
            </div>

            <button
              type="button"
              className={`${styles.btn} ${styles.btnDanger}`}
              onClick={onSignOut}
              disabled={busy}
            >
              Sign out
            </button>

            <p className={styles.hint}>
              Manage photo, name, and access on the profile page.
            </p>

            {err ? <p className={styles.error}>{err}</p> : null}

            <input
              ref={fileRef}
              className={styles.fileInput}
              type="file"
              accept="image/*"
              onChange={onFileChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

